import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Handshake, TrendingUp, DollarSign, Users, ArrowRight, ExternalLink, LogIn } from "lucide-react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useNoindex } from "@/hooks/use-seo";
import { marketingTitle } from "@/data/marketing-seo";
import { partnerPortalPath, partnerPortalUrl } from "@/lib/partner-portal-url";
import { PartnerFooter } from "@/components/partner-footer";

const BROKER_PORTAL_URL = partnerPortalPath("/brokers");

const BENEFITS = [
  { icon: Handshake, title: "Referrals from PadPulse closing teams", desc: "Receive qualified referrals directly from PadPulse closing teams. No cold outreach — deals come to you." },
  { icon: TrendingUp, title: "Track every deal through close", desc: "Your partner portal gives you a clear view of every active referral, its status, and where it is in the pipeline." },
  { icon: DollarSign, title: "Commission visibility at every stage", desc: "See exactly what you've earned and what's pending, updated in real time as deals move through approval and close." },
  { icon: Users, title: "Also a PadPulse affiliate", desc: "As a partner you're enrolled as a PadPulse affiliate — earn commissions when you refer new users to PadPulse." },
];

export default function PartnerBrokersPage() {
  usePageTitle(marketingTitle("Broker Partners"), true);
  // The partner program is frozen (build F8): pages stay, but are noindexed and unlinked.
  useNoindex();

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <span className="font-bold text-slate-900">PadPulse</span>
          </a>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <Link href="/partners/lenders">Lenders</Link>
            <Link href="/partners/wholesalers">Wholesalers</Link>
            <Link href="/partners/realtors">Realtors</Link>
            <a href={partnerPortalUrl()} className="text-blue-600 font-medium flex items-center gap-1" target="_blank" rel="noopener noreferrer">
              Partner portal <ExternalLink className="h-3 w-3" />
            </a>
          </nav>
        </div>
      </header>

      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            Broker Partners
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Manage referrals and track every loan deal
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">
            Receive referrals from PadPulse closing teams and track every deal from approval to close. Your dedicated partner portal keeps commission tracking, deal management, and messaging all in one place.
          </p>
          <Button size="lg" onClick={() => document.getElementById("signin")?.scrollIntoView({ behavior: "smooth" })} data-testid="button-get-started">
            Broker sign-in <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <section className="py-16 bg-slate-50 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-10">Why brokers choose PadPulse</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {BENEFITS.map((b) => (
              <Card key={b.title}>
                <CardContent className="p-6 flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <b.icon className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">{b.title}</p>
                    <p className="text-sm text-slate-500">{b.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="signin" className="py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-5">
                <LogIn className="h-6 w-6 text-amber-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Already a PadPulse broker partner?</h2>
              <p className="text-slate-500 mb-6">
                Broker partnerships are initiated by PadPulse closing teams — there's no separate application to fill out. Once a PadPulse team adds you as a broker partner, you'll get access to your portal to manage referrals, track deals, and view commissions. Sign in below.
              </p>
              <a href={BROKER_PORTAL_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" data-testid="button-broker-signin">
                  Sign in to broker portal <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <p className="text-xs text-slate-400 mt-4">Not yet partnered with a PadPulse closing team? You'll be added automatically when a deal is referred your way.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <PartnerFooter />
    </div>
  );
}
